import {Inject, Injectable} from '@angular/core';
import {Observable, BehaviorSubject, of, pipe} from 'rxjs';
import { catchError, tap, map } from 'rxjs/operators';
import {UrlKey, IHttpService, WebLogger} from "@weps/shared-http";
import { GatekeeperFeature } from './gatekeeper.model';
import {environment} from "@environments/environment";

@Injectable({
  providedIn: 'root'
})
export class GatekeeperService {
  private features = new BehaviorSubject<GatekeeperFeature[]>([]);
  private initialized = false;
  private featureIds: string = "ENABLE_TRUST_AS_OWNER,ENABLE_JOINT_AS_OWNER,ENABLE_BENEFICIARIES"
  constructor(@Inject("IHttpService") private httpService: IHttpService){}

  initializeFeatures(): Observable<GatekeeperFeature[]> {
    if (this.initialized) {
      WebLogger.log({
        message: 'Gatekeeper features already initialized, returning cached features',
        level: 'debug',
        payload: {
          event_type: 'gatekeeper_cache_hit',
          feature_count: this.features.value.length
        }
      });
      return this.features.asObservable();
    }

    const urlParams = {
      featureIds: this.featureIds,
      env: environment.gatekeeper_env
    };

    WebLogger.log({
      message: 'Initializing gatekeeper features',
      level: 'info',
      payload: {
        event_type: 'gatekeeper_init_start',
        requested_features: this.featureIds,
        environment: environment.gatekeeper_env
      }
    });

    return this.httpService.doSimpleGetRequest<GatekeeperFeature[]>(UrlKey.gatekeeeper_url, urlParams)
      .pipe(
        map((response: unknown) => {
          if (Array.isArray(response) &&
            response.every(item =>
              typeof item === 'object' &&
              'featureId' in item &&
              'enabled' in item
            )) {
            return response as GatekeeperFeature[];
          }
          WebLogger.log({
            message: 'Invalid gatekeeper feature response format',
            level: 'error',
            payload: {
              event_type: 'gatekeeper_invalid_response',
              response
            }
          });
          throw new Error('Invalid feature response format');
        }),
        tap(features => {
          this.features.next(features);
          this.initialized = true;

          // Log feature states for observability
          const featureStates = features.reduce((acc, feature) => {
            acc[feature.featureId] = {
              enabled: feature.enabled,
              lastToggledDate: feature.lastToggledDate
            };
            return acc;
          }, {} as Record<string, any>);

          WebLogger.log({
            message: 'Gatekeeper features initialized successfully',
            level: 'info',
            payload: {
              event_type: 'gatekeeper_init_success',
              feature_count: features.length,
              feature_states: featureStates,
              beneficiaries_enabled: features.find(f => f.featureId === 'ENABLE_BENEFICIARIES')?.enabled ?? false,
              trust_owner_enabled: features.find(f => f.featureId === 'ENABLE_TRUST_AS_OWNER')?.enabled ?? false,
              joint_owner_enabled: features.find(f => f.featureId === 'ENABLE_JOINT_AS_OWNER')?.enabled ?? false
            }
          });
        }),
        catchError(error => {
          WebLogger.log({
            message: "Gatekeeper features fetch error",
            level: "error",
            payload: {
              event_type: 'gatekeeper_init_error',
              error: error?.message || error,
              requested_features: this.featureIds,
              environment: environment.gatekeeper_env
            }
          });
          return of([]);
        })
      );
  }

  getFeatures(): Observable<GatekeeperFeature[]> {
    return this.initialized ? this.features.asObservable() : this.initializeFeatures();
  }

  getFeatureById(featureIds: string): Observable<GatekeeperFeature | undefined> {
    return this.getFeatures().pipe(
      map(features => features.find(f => f.featureId === featureIds))
    );
  }

  isTrustFeatureEnabled(): Observable<boolean> {
    return this.getFeatureById('ENABLE_TRUST_AS_OWNER').pipe(
      map(feature => feature?.enabled ?? false)
    );
  }

  isBeneficiariesFeatureEnabled(): Observable<boolean> {
    return this.getFeatureById('ENABLE_BENEFICIARIES').pipe(
      map(feature => {
        const enabled = feature?.enabled ?? false;
        WebLogger.log({
          message: 'Checking ENABLE_BENEFICIARIES feature state',
          level: 'debug',
          payload: {
            event_type: 'gatekeeper_beneficiaries_check',
            enabled,
            feature_present: !!feature,
            last_toggled: feature?.lastToggledDate
          }
        });
        return enabled;
      })
    );
  }

  isJointOwnerFeatureEnabled(): Observable<boolean> {
    return this.getFeatureById('ENABLE_JOINT_AS_OWNER').pipe(
      map(feature => feature?.enabled ?? false)
    );
  }
}
